import React, { memo, useMemo } from "react";
import { ReactSVG } from "react-svg";

import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { getExpenseTypesAPI } from "services/api/finance/expense-types/api";
import { ExpenseAppliesTo, ExpenseType } from "services/api/finance/expense-types/types";
import { DEFAULT_EXPENSE_TYPE_ICON, EXPENSE_TYPE_ICONS } from "services/api/fleet/vehicles/expenses/consts";
import { buildDropdownCacheKey, DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";
import { EXPENSE_TYPES_CACHE_TTL } from "ui/components/Dropdown/ExpenseTypeDropdown/consts";
import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";
import CheckIcon from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/scenes/CheckIcon";

import { EXPENSE_INTRO_SAMPLE, EXPENSE_INTRO_STEP_TEXTS, EXPENSE_INTRO_TEXTS } from "./consts";
import { ExpenseIntroProps } from "./types";

const fetchVehicleExpenseTypes = () => getExpenseTypesAPI(ExpenseAppliesTo.VEHICLE);

/**
 * Lijevi panel unosa troška vozila: kartica troška s vrstom, iznosom i PDV-om, priloženi
 * računi i obavijest da trošak ide na vozilo. Modal ima jedan korak, pa nema točkica.
 */
const ExpenseIntro: React.FC<ExpenseIntroProps> = ({ formValues, uploads, isEdit, stepCurrent, stepCount }) => {
  // Isti keš kao dropdown vrste troška, pa nema dodatnog zahtjeva.
  const { data } = useDropdownCache<ExpenseType[]>(
    buildDropdownCacheKey(DropdownCacheKey.ExpenseTypes, ExpenseAppliesTo.VEHICLE),
    fetchVehicleExpenseTypes,
    EXPENSE_TYPES_CACHE_TTL
  );

  const expenseType = useMemo(
    () => (data ?? []).find((type) => type.id === formValues.expenseTypeId),
    [data, formValues.expenseTypeId]
  );

  const text = EXPENSE_INTRO_STEP_TEXTS[stepCurrent] ?? (isEdit ? EXPENSE_INTRO_TEXTS.edit : EXPENSE_INTRO_TEXTS.create);
  const typeName = expenseType?.name ?? EXPENSE_INTRO_SAMPLE.typeName;
  const typeCode = expenseType ? expenseType.code : EXPENSE_INTRO_SAMPLE.typeCode;
  const typeIcon = (typeCode && EXPENSE_TYPE_ICONS[typeCode]) || DEFAULT_EXPENSE_TYPE_ICON;

  const hasAmount = formValues.amount !== null || formValues.taxAmount !== null;
  const amount = hasAmount ? formValues.amount ?? 0 : EXPENSE_INTRO_SAMPLE.amount;
  const taxAmount = hasAmount ? formValues.taxAmount ?? 0 : EXPENSE_INTRO_SAMPLE.taxAmount;
  const expenseDate = transformDateToLocalDateString(formValues.expenseDate ?? new Date());

  const files = uploads.length > 0 ? uploads.map((upload) => upload.originalFileName) : EXPENSE_INTRO_SAMPLE.files;

  return (
    <ShowcaseIntro
      sceneKey={stepCurrent}
      title={text.title}
      description={text.description}
      stepCurrent={stepCurrent}
      stepCount={stepCount}
    >
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-11 h-11 rounded-md bg-blue-50 text-blue-700 flex-shrink-0">
              <ReactSVG src={typeIcon} className="flex w-5 h-5 [&_svg]:w-full [&_svg]:h-full" />
            </span>
            <div className="min-w-0">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400 truncate">{typeName}</div>
              <div className="text-sm font-bold text-slate-800 truncate">{formValues.name || EXPENSE_INTRO_SAMPLE.name}</div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {[
              { label: "Datum troška", value: expenseDate },
              { label: "Iznos", value: formatCurrency.format(amount) },
              { label: "PDV", value: formatCurrency.format(taxAmount) },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-[0.7rem]">
                <span className="text-slate-500">{row.label}</span>
                <span className="font-semibold text-slate-800 truncate">{row.value}</span>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-[0.7rem] font-semibold text-slate-500">Ukupno</span>
            <span className="text-base font-bold text-slate-800">{formatCurrency.format(amount + taxAmount)}</span>
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Prilozi</div>
          <div className="mt-2 space-y-1.5">
            {files.slice(0, 3).map((file, index) => (
              <div key={index} className="flex items-center gap-2 text-[0.7rem]">
                <CheckIcon />
                <span className="font-medium text-slate-700 truncate">{file}</span>
              </div>
            ))}
          </div>
          {files.length > 3 && <div className="mt-2 text-[0.65rem] text-slate-400">i još {files.length - 3}</div>}
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Trošak ide na vozilo"
          subtitle="Vidi se na tabu Troškovi"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          }
        />
      </ShowcaseCard>
    </ShowcaseIntro>
  );
};

export default memo(ExpenseIntro);
